import React, { useEffect, useState } from 'react';

import { FormSectionDefaultProps, FormSectionPropTypes } from 'src/components/JobDatasheet/propTypes';
import { getDescendantQuestion, getValidationErrors as getValidationErrorsForSubSection, getSectionLevelClassName } from 'src/components/JobDatasheet/utils';
import { getSectionQuestions, getSectionResults } from 'src/components/JobDatasheet/JobDatasheetForm/utils';
import { QuestionRow } from 'src/components/JobDatasheet/QuestionRow';
import s from 'src/components/JobDatasheet/JobDatasheetForm/JobDatasheetForm.styles';
import { getValidationVariables } from 'src/utils/form/datasheet';

export const FormSectionComponent = props => {
  const {
    applyQuestionConditions,
    collapsedSections,
    isInfoExpanded,
    currentFilter,
    fromJob,
    getResult,
    getValidationErrors,
    getNumberOfAttachments,
    indexPath,
    isAdditionalReportingDocuments,
    isBlockSaveFailed,
    isBlockSaveSuccess,
    job,
    negativeOutComeResultIds,
    readonly,
    section,
    sectionsOptions,
    selectedQuestion,
    setResult,
    statistic,
    toggleSection,
    toggleSelectedQuestion,
    wrapperRef,
    isInternalUser,
    allSections,
    isQuesPresentInMultipleDatasheets,
    results,
    toggleOpenComment,
    showError,
    activeQuestion,
  } = props;

  const allQuestions = (allSections || []).map(c => c.questions).flat(1);
  const [sectionHeight, setSectionHeight] = useState(0);

  useEffect(() => {
    const sectionHeightCurrent = wrapperRef?.current?.getBoundingClientRect()?.height || null;
    setSectionHeight(sectionHeightCurrent);
  }, [wrapperRef?.current]);

  const dynamicStyles = {
    minHeight: (sectionHeight && isInfoExpanded
      ) ? `calc(${sectionHeight}px + 315px)` : 'auto', 
  };

  const number = `${indexPath.map(v => v + 1).join('.')}${
    indexPath.length > 0 ? '.' : ''
  }${section.position + 1}.`;
  const isCollapsedSection =
    collapsedSections.indexOf(section.originalIndex) !== -1;

  const questions = getSectionQuestions(
    section,
    applyQuestionConditions(section),
    currentFilter,
    statistic,
    negativeOutComeResultIds,
    getResult
  );

  const descendantQuestions = getDescendantQuestion(section, questions);
  const descendantQuestionIds = descendantQuestions.map(q => q.id);

  const sectionHasRequiredAction =
    statistic ? statistic.actionRequiredtypeQuestionIds.some(id => descendantQuestionIds.includes(id)) : false;
  
  const renderSectionTitle = () => (
    <>
      {sectionHasRequiredAction && <s.RedDot />}
      <s.SectionTitle
        className={fromJob ? getSectionLevelClassName(indexPath?.length ?? 0) : ''}
        onClick={() => toggleSection(section.originalIndex)}
        fromJob={fromJob}
      >
        <s.SectionNumber fromJob={fromJob}>{number}</s.SectionNumber>
        <s.SectionLabel fromJob={fromJob}>
          {section.name}
          <s.SectionToggleBtn fromJob={fromJob} collapsed={isCollapsedSection} name="expand" />
        </s.SectionLabel>
      </s.SectionTitle>
    </>
  );
  
  if (section.children.length > 0) {
    return (
      <s.SectionWrapper
        key={section.position}
        collapsed={isCollapsedSection}
        level2={indexPath.length > 1 || 'false'}
        ref={wrapperRef}
        style={(section.position === allSections.length - 1) ? dynamicStyles : {}}
        root={indexPath.length === 0}
        fromJob={fromJob}
        >
        {renderSectionTitle()}
        {!isCollapsedSection &&
          section.children.map(subSection => {
            const sectionResults = getSectionResults(subSection, results);
            const variables = getValidationVariables(sectionResults);
            return <FormSection
              key={subSection.originalIndex}
              {...props}              
              getValidationErrors={question => getValidationErrorsForSubSection(question, variables)}
              indexPath={[...indexPath, section.position]}
              section={subSection}
              wrapperRef={sectionsOptions.find(c => c.value === subSection.originalIndex).ref}
            />
    })}   
  </s.SectionWrapper>
    );
  }

  return (
    <s.SectionWrapper
      key={section.position}
      collapsed={isCollapsedSection}
      level2={indexPath.length > 1 || 'false'}
      ref={wrapperRef}
      root={indexPath.length === 0}
      style={(section.position === allSections.length - 1) ? dynamicStyles : {}}
      fromJob={fromJob}
      >
      {renderSectionTitle()}
      {!isCollapsedSection && questions?.map(question => {
        const questionResult = getResult(question);
        return (
          <QuestionRow
            key={question.id}
            activeQuestion={activeQuestion}
            fromJob={fromJob}
            getNumberOfAttachments={getNumberOfAttachments}
            getValidationErrors={getValidationErrors}
            isAdditionalReportingDocuments={isAdditionalReportingDocuments}
            isBlockSaveFailed={isBlockSaveFailed}
            isBlockSaveSuccess={isBlockSaveSuccess}
            isQuesPresentInMultipleDatasheets={()=>isQuesPresentInMultipleDatasheets(question)}
            job={job}
            question={question}
            readonly={readonly}
            result={questionResult.result}
            getResult={getResult}
            allQuestions={allQuestions}
            sectionOriginalIndex={section.originalIndex}
            selectedQuestion={selectedQuestion}
            setResult={setResult}
            statistic={statistic}
            toggleSelectedQuestion={toggleSelectedQuestion}
            isInternalUser={isInternalUser}
            isPrefilled={questionResult?.isPrefilled}
            toggleOpenComment={toggleOpenComment}
            showError={showError}
          />
        )
      })}
    </s.SectionWrapper>
  );
};

FormSectionComponent.propTypes = FormSectionPropTypes;
FormSectionComponent.defaultProps = FormSectionDefaultProps;

export const FormSection = React.memo(FormSectionComponent);
